import {
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import ContentCopy from '@mui/icons-material/ContentCopy'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Warning from '@mui/icons-material/Warning'
import Person from '@mui/icons-material/Person'
import VideoCall from '@mui/icons-material/VideoCall'
import { default as LinkIcon } from '@mui/icons-material/Link'
import AccessTime from '@mui/icons-material/AccessTime'
import Security from '@mui/icons-material/Security'
import Public from '@mui/icons-material/Public'
import React, { useState } from 'react'
import { copyToClipBoard, getExactTimeDiff } from 'src/consumer/helpers/utilFunctions'
import { buildPublicSessionJoinLink } from 'src/consumer/helpers/publicSessionLinks'
import { formatEventTimeRange } from 'src/supportPortal/components/common/bookedEventInfo/components/utils'
import { ALL_GENDERS, APP_CONSTANTS, EVENT_TYPES_ARRAY } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import SendJoiningLinkButtons from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/SendJoiningLinkButtons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { styled } from '@mui/material/styles'
import { toast } from 'react-toastify'

dayjs.extend(utc)

// Styled Components
const LinkCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(67, 56, 202, 0.15)',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.02) 100%)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(67, 56, 202, 0.3)',
    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.1)',
  },
}))

const LinkDisplayBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  background: '#fff',
  borderRadius: '8px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(67, 56, 202, 0.04)',
    borderColor: 'rgba(67, 56, 202, 0.2)',
  },
}))

const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  borderRadius: '8px',
  background: 'rgba(67, 56, 202, 0.1)',
  color: '#4338ca',
  '&:hover': {
    background: 'rgba(67, 56, 202, 0.2)',
  },
}))

export default function JoiningDetails({
  bookedEventInfo,
  closeDialog,
  sendSecureSessionLinkToPatientSignature = '/api/secure/cc/support/events/secure-join-link/send',
  sendPublicSessionLinkToPatientSignature = '/api/secure/cc/support/events/public-join-link/send',
  showSendingLinkButtonsToSupport = false,
}) {
  const [copiedSecure, setCopiedSecure] = useState(false)
  const [copiedPublic, setCopiedPublic] = useState(false)

  const patientInfo = bookedEventInfo?.patientInfo
  const publicBookedSessionLinkInfo = bookedEventInfo?.publicJoiningLinks?.[0]
  const isPublicNotValidYet = dayjs.utc(publicBookedSessionLinkInfo?.validFrom).isAfter(dayjs())
  const isPublicLinkExpired = dayjs.utc(publicBookedSessionLinkInfo?.expiresAt).isBefore(dayjs())

  const currentEventType = EVENT_TYPES_ARRAY?.find((event) => {
    return event?.id === bookedEventInfo?.eventType
  })
  const secureSessionJoinLink = `${APP_CONSTANTS.LIVE_APP_URL}/l/${bookedEventInfo?.slug}`
  const publicSessionJoinLink = buildPublicSessionJoinLink(publicBookedSessionLinkInfo?.sessionSlug)

  function copySecureSessionJoinLink() {
    copyToClipBoard(secureSessionJoinLink, secureSessionJoinLink)
    setCopiedSecure(true)
    toast.success('Secure link copied to clipboard')
    setTimeout(() => setCopiedSecure(false), 2000)
  }

  function copyPublicSessionJoinLink() {
    copyToClipBoard(publicSessionJoinLink, publicSessionJoinLink)
    setCopiedPublic(true)
    toast.success('Public share link copied to clipboard')
    setTimeout(() => setCopiedPublic(false), 2000)
  }

  return (
    <>
      <DialogTitle
        sx={{
          padding: '24px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#4338ca', marginBottom: '4px' }}>
              Joining Details
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', marginTop: '8px' }}>
              <Chip
                icon={<AccessTime sx={{ fontSize: 16 }} />}
                label={formatEventTimeRange(bookedEventInfo)}
                size="small"
                sx={{
                  background: 'rgba(67, 56, 202, 0.1)',
                  color: '#4338ca',
                  fontWeight: 500,
                }}
              />
              {currentEventType?.title && (
                <Chip
                  icon={<VideoCall sx={{ fontSize: 16 }} />}
                  label={currentEventType?.title}
                  size="small"
                  sx={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: '#6366f1',
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={closeDialog}
            sx={{
              color: 'rgba(0, 0, 0, 0.6)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        {/* Two Column Layout */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Left Column - Practitioner */}
          <Box>
            <LinkCard>
              <CardContent sx={{ padding: '20px !important' }}>
                {/* Practitioner Info */}
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Practitioner
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, marginTop: '4px', color: '#4338ca' }}>
                    {bookedEventInfo?.mentorName}
                  </Typography>
                </Box>

                <Divider sx={{ marginBottom: '16px' }} />

                {/* Secure Link */}
                <Box sx={{ marginBottom: '16px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '8px' }}>
                    <Security sx={{ color: '#4338ca', fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4338ca' }}>
                      Secure Link
                    </Typography>
                    <Chip
                      label="Sign In Required"
                      size="small"
                      sx={{
                        background: 'rgba(67, 56, 202, 0.1)',
                        color: '#4338ca',
                        fontSize: '9px',
                        height: '18px',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <LinkDisplayBox onClick={copySecureSessionJoinLink}>
                    <LinkIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 16 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: 'rgba(0, 0, 0, 0.8)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {secureSessionJoinLink}
                    </Typography>
                    <Tooltip title={copiedSecure ? 'Copied!' : 'Copy link'}>
                      <CopyButton size="small" onClick={(e) => { e.stopPropagation(); copySecureSessionJoinLink(); }}>
                        {copiedSecure ? <CheckCircle sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                      </CopyButton>
                    </Tooltip>
                  </LinkDisplayBox>
                </Box>

                {/* Send Secure Link Buttons */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    Send Secure Link
                  </Typography>
                  <SendJoiningLinkButtons
                    link={secureSessionJoinLink}
                    bookedEventInfo={bookedEventInfo}
                    linkSendingApiSignature={sendSecureSessionLinkToPatientSignature}
                    showSendingLinkButtonsToSupport={showSendingLinkButtonsToSupport}
                    sendLinkToHumanSupportNumberApiSignature={
                      '/api/secure/cc/support/events/secure-join-link/send-to-human-support'
                    }
                  />
                </Box>
              </CardContent>
            </LinkCard>
          </Box>

          {/* Right Column - Patient */}
          <Box>
            <LinkCard>
              <CardContent sx={{ padding: '20px !important' }}>
                {/* Patient Info */}
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Patient
                  </Typography>
                  {patientInfo ? (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 600, marginTop: '4px' }}>
                        {patientInfo?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginTop: '2px' }}>
                        {patientInfo?.patientNumber} • {getAgeFromDobWithTime(patientInfo?.dateOfBirth)}/
                        {ALL_GENDERS?.find((gndr) => gndr?.id === patientInfo?.gender)?.title}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600, marginTop: '4px' }}>
                      {bookedEventInfo?.userName}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ marginBottom: '16px' }} />

                {/* Public Share Link */}
                <Box sx={{ marginBottom: '16px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '8px', flexWrap: 'wrap' }}>
                    <Public sx={{ color: '#10b981', fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#10b981' }}>
                      Public Share Link
                    </Typography>
                    <Chip
                      label="No Sign In"
                      size="small"
                      sx={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981',
                        fontSize: '9px',
                        height: '18px',
                        fontWeight: 600,
                      }}
                    />
                    {isPublicLinkExpired && (
                      <Chip
                        icon={<Warning sx={{ fontSize: 12 }} />}
                        label="Expired"
                        size="small"
                        color="error"
                        sx={{ fontSize: '9px', height: '18px', fontWeight: 600 }}
                      />
                    )}
                    {isPublicNotValidYet && (
                      <Chip
                        icon={<AccessTime sx={{ fontSize: 12 }} />}
                        label={`Valid in ${getExactTimeDiff(
                          dayjs.utc(publicBookedSessionLinkInfo?.validFrom).local(),
                          undefined,
                          true,
                          false,
                        )}`}
                        size="small"
                        color="warning"
                        sx={{ fontSize: '9px', height: '18px', fontWeight: 600 }}
                      />
                    )}
                  </Box>

                  {isPublicLinkExpired && (
                    <Alert severity="warning" sx={{ marginBottom: '8px', borderRadius: '6px', fontSize: '11px', padding: '8px' }}>
                      Link expired
                    </Alert>
                  )}

                  {isPublicNotValidYet && (
                    <Alert severity="info" sx={{ marginBottom: '8px', borderRadius: '6px', fontSize: '11px', padding: '8px' }}>
                      Valid in {getExactTimeDiff(
                        dayjs.utc(publicBookedSessionLinkInfo?.validFrom).local(),
                        undefined,
                        true,
                        false,
                      )}
                    </Alert>
                  )}

                  <LinkDisplayBox onClick={copyPublicSessionJoinLink}>
                    <LinkIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 16 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: isPublicLinkExpired ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.8)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textDecoration: isPublicLinkExpired ? 'line-through' : 'none',
                      }}
                    >
                      {publicSessionJoinLink}
                    </Typography>
                    <Tooltip title={copiedPublic ? 'Copied!' : 'Copy link'}>
                      <CopyButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          copyPublicSessionJoinLink()
                        }}
                        disabled={isPublicLinkExpired}
                        sx={{
                          ...(isPublicLinkExpired && {
                            background: 'rgba(0, 0, 0, 0.05)',
                            color: 'rgba(0, 0, 0, 0.3)',
                          }),
                        }}
                      >
                        {copiedPublic ? <CheckCircle sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                      </CopyButton>
                    </Tooltip>
                  </LinkDisplayBox>
                </Box>

                {/* Send Public Share Link Buttons */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '8px',
                    }}
                  >
                    Send Public Share Link
                  </Typography>
                  <SendJoiningLinkButtons
                    link={publicSessionJoinLink}
                    bookedEventInfo={bookedEventInfo}
                    linkSendingApiSignature={sendPublicSessionLinkToPatientSignature}
                    showSendingLinkButtonsToSupport={showSendingLinkButtonsToSupport}
                    sendLinkToHumanSupportNumberApiSignature={
                      '/api/secure/cc/support/events/public-join-link/send-to-human-support'
                    }
                  />
                </Box>
              </CardContent>
            </LinkCard>
          </Box>
        </Box>
      </DialogContent>
    </>
  )
}
